/**
 * Search suggestions under the header's search field: product rows from the
 * Predictive Search API while typing, the popular and recent searches when the
 * field is empty.
 */
const RECENT_KEY = 'sda-recent-searches';
const RECENT_MAX = 3;
const DEBOUNCE = 250;

class PredictiveSearch extends HTMLElement {
  #controller = new AbortController();
  #request;
  #timer;
  #input;
  #panel;
  #results;
  #clear;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#input = this.querySelector('input[type="search"]');
    this.#panel = this.querySelector('.sda-search-panel');
    this.#results = this.querySelector('[data-search-results]');
    this.#clear = this.querySelector('.sda-search__clear');

    this.#input.addEventListener('input', () => this.#onInput(), { signal });
    this.#input.addEventListener('focus', () => this.#open(), { signal });
    this.#clear.addEventListener('click', () => this.#reset(), { signal });
    this.querySelector('form').addEventListener('submit', () => remember(this.#input.value), { signal });
    this.addEventListener('keydown', (event) => event.key === 'Escape' && this.#close(true), { signal });
    // Putting the phone keyboard away blurs the field without moving focus anywhere; the panel stays.
    this.addEventListener(
      'focusout',
      ({ relatedTarget }) => relatedTarget && !this.contains(relatedTarget) && this.#close(),
      { signal }
    );
    document.addEventListener('pointerdown', (event) => this.contains(event.target) || this.#close(), { signal });

    this.#clear.hidden = !this.#input.value;
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#request?.abort();
  }

  #open() {
    this.#renderRecent();
    this.#panel.hidden = false;
  }

  #close(returnFocus = false) {
    this.#panel.hidden = true;
    if (returnFocus) this.#input.focus();
  }

  #reset() {
    this.#input.value = '';
    this.#input.focus();
    this.#onInput();
  }

  #onInput() {
    const terms = this.#input.value.trim();
    this.#clear.hidden = !this.#input.value;
    clearTimeout(this.#timer);
    this.#request?.abort();

    if (!terms) {
      this.#results.hidden = true;
      this.#results.replaceChildren();
      return this.#open();
    }

    this.#timer = setTimeout(() => this.#search(terms), DEBOUNCE);
  }

  async #search(terms) {
    this.#request = new AbortController();
    const { signal } = this.#request;
    const query = encodeURIComponent(terms);
    const suggestions = `${this.dataset.url}?q=${query}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;
    const count = `${this.dataset.searchUrl}?q=${query}&type=product&options[prefix]=last&section_id=search-results-count`;

    this.#results.setAttribute('aria-busy', 'true');
    try {
      const [html, allResults] = await Promise.all([fetchSection(suggestions, signal), fetchSection(count, signal)]);
      this.#results.replaceChildren(...html.childNodes);
      for (const title of this.#results.querySelectorAll('.sda-row__name')) highlight(title, terms);

      const label = allResults.textContent.trim();
      const link = this.#results.querySelector('[data-search-all]');
      if (link && label) link.textContent = label;

      this.#results.hidden = false;
      this.#open();
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    } finally {
      this.#results.removeAttribute('aria-busy');
    }
  }

  #renderRecent() {
    const section = this.querySelector('[data-search-recent]');
    const terms = this.#input.value.trim() ? [] : recent();
    const template = this.querySelector('template[data-search-recent-chip]');

    section.hidden = terms.length === 0;
    section.querySelector('.sda-chips').replaceChildren(
      ...terms.map((term) => {
        const chip = template.content.firstElementChild.cloneNode(true);
        chip.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}&type=product&options[prefix]=last`;
        chip.append(term);
        return chip;
      })
    );
  }
}

/** Fetches a rendered section and returns its content as a document fragment's root. */
async function fetchSection(url, signal) {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
  const section = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('.shopify-section');
  return section ?? document.createElement('div');
}

/** Wraps the typed words in <mark>, built from text nodes so no HTML is injected. */
function highlight(element, terms) {
  const words = terms.split(/\s+/).filter(Boolean).map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (words.length === 0) return;

  const parts = element.textContent.split(new RegExp(`(${words.join('|')})`, 'gi'));
  element.replaceChildren(
    ...parts.map((part, index) => {
      if (index % 2 === 0) return part;
      const mark = document.createElement('mark');
      mark.textContent = part;
      return mark;
    })
  );
}

function recent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) ?? [];
  } catch {
    return [];
  }
}

function remember(value) {
  const term = value.trim();
  if (!term) return;
  try {
    const terms = [term, ...recent().filter((other) => other.toLowerCase() !== term.toLowerCase())].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(terms));
  } catch {
    // Storage can be unavailable in private windows; recent searches are a convenience only.
  }
}

customElements.define('predictive-search', PredictiveSearch);
